import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, getServerTimeOffsetMs } from '@/api/client';
import { useShellNav } from '@/stores/shellNav';
import { useCompany } from '@/stores/company';
import { usePreferences } from '@/stores/preferences';
import { CalendarIcon } from './ModuleIcon';


/** Current time corrected for the offset between this machine's clock and
 *  the server's — farm-site PCs are not guaranteed to be right. */
function correctedNow(): Date {
  return new Date(Date.now() + getServerTimeOffsetMs());
}

/**
 * The topbar clock: day, date and time in Pakistan time, regardless of the
 * viewer's machine timezone or clock accuracy. No Hijri date, by design —
 * the calendar screen is where Islamic dates live.
 *
 * It is also the way to the calendar. It carries the calendar's own icon so
 * that reads as a button rather than a label; a second Calendar button beside
 * it went to the same screen and only added to the clutter.
 */
export function HeaderClock({ onCalendar = false }: { onCalendar?: boolean }) {
  const language = usePreferences((s) => s.prefs.language);
  // Time zone and 12/24 hour clock come from Settings › Company.
  const timeZone = useCompany((s) => s.company.timezone);
  const hour12 = useCompany((s) => s.company.timeFormat) === '12h';
  const navigate = useShellNav((s) => s.navigate);
  const [now, setNow] = useState(correctedNow);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function scheduleNextTick() {
      const current = correctedNow();
      setNow(current);
      // Land just after the next minute boundary, not on a fixed interval,
      // so the displayed minute never lags behind the real one.
      const msIntoMinute =
        current.getSeconds() * 1000 + current.getMilliseconds();
      const msToNextMinute = 60_000 - msIntoMinute + 50;
      timeoutId = setTimeout(scheduleNextTick, msToNextMinute);
    }
    scheduleNextTick();

    // A backgrounded tab throttles timers, so re-sync the moment it is
    // visible again rather than waiting for a timeout that may have slipped.
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') setNow(correctedNow());
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Re-fetched automatically when the Karachi calendar date rolls over,
  // since that changes the query key.
  const karachiDate = new Intl.DateTimeFormat('en-CA', { timeZone }).format(now);
  const holidayToday = useQuery({
    queryKey: ['calendar', 'today', karachiDate],
    queryFn: () => api.get('/api/v1/calendar/today'),
    staleTime: 60_000,
  });
  const holiday = holidayToday.data?.[0];

  const locale = language === 'ur' ? 'ur-PK' : 'en-PK';
  const full = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour12,
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(now);
  const short = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour12,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(now);

  return (
    <button
      className="headerclock"
      onClick={() => navigate('home-calendar')}
      title="Open calendar"
      aria-current={onCalendar ? 'page' : undefined}
    >
      <span className="headerclock__icon" aria-hidden="true">
        <CalendarIcon />
      </span>
      {/* Said first, so the button announces what it does and then the date.
          An aria-label here would hide the date instead of explaining it. */}
      <span className="sr-only">Open calendar. </span>
      <time dateTime={now.toISOString()} className="headerclock__full">
        {full}
      </time>
      <time dateTime={now.toISOString()} className="headerclock__short">
        {short}
      </time>
      {holiday && (
        <span className="headerclock__holiday">
          {language === 'ur' && holiday.nameUr
            ? holiday.nameUr
            : holiday.nameEn}
          {holiday.status === 'EXPECTED' && ' (expected)'}
        </span>
      )}
    </button>
  );
}
